import {
  getReferralsMe,
  getReferralsValidate,
  patchReferralsMe,
  postReferralsMe,
  postReferralsSignup,
} from "../api/endpoints/referral/referral";
import type {
  MyReferral,
  ReferralCode,
  StartReferralSignupDTO,
  StartReferralSignupResponse,
  ValidatedReferralCode,
} from "../api/models";

/**
 * Camada fina sobre os endpoints `/referrals/*` gerados por orval.
 *
 * O mutator `customFetch` nunca lança: ele devolve `{ data, status, headers }`
 * inclusive nos erros. Aqui os status de erro viram `ReferralApiError`, para
 * que as telas usem `try/catch` e os estados de erro do TanStack Query.
 *
 * Só vale para o envelope da API (`{ success, data, ... }`). As rotas
 * `/api/auth/*` (Better Auth) devolvem o objeto cru e continuam no `authClient`.
 */

/** Indicação já com o código gerado (o `MyReferral` do spec inclui `null`). */
export type MyReferralData = NonNullable<MyReferral>;

interface ApiErrorDetail {
  code?: string;
  message?: string;
  type?: string;
  path?: string[];
}

interface ApiErrorEnvelope {
  success?: boolean;
  message?: string;
  error?: {
    statusCode?: number;
    code?: string;
    name?: string;
    details?: ApiErrorDetail[] | null;
  };
  requestId?: string | null;
}

/** Erro normalizado das rotas `/referrals/*`. */
export class ReferralApiError extends Error {
  readonly statusCode: number;
  /** Código de negócio da API (ex.: `E_REFERRAL_CODE_NOT_FOUND`). */
  readonly code: string | null;
  /** Erros por campo, derivados de `error.details[].path`. */
  readonly fieldErrors: Record<string, string>;
  /** Segundos do header `retry-after`, quando o CORS expõe o header. */
  readonly retryAfterSeconds: number | null;
  readonly requestId: string | null;

  constructor(params: {
    message: string;
    statusCode: number;
    code?: string | null;
    fieldErrors?: Record<string, string>;
    retryAfterSeconds?: number | null;
    requestId?: string | null;
  }) {
    super(params.message);
    this.name = "ReferralApiError";
    this.statusCode = params.statusCode;
    this.code = params.code ?? null;
    this.fieldErrors = params.fieldErrors ?? {};
    this.retryAfterSeconds = params.retryAfterSeconds ?? null;
    this.requestId = params.requestId ?? null;
  }

  /** Código de indicação inexistente ou desativado. */
  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /** Payload inválido (ex.: e-mail malformado). */
  get isValidation(): boolean {
    return this.statusCode === 400;
  }

  /** Rate limit por IP estourado. */
  get isRateLimited(): boolean {
    return this.statusCode === 429;
  }

  /** O usuário já possui um código de indicação. */
  get isAlreadyExists(): boolean {
    return this.statusCode === 409;
  }

  /** Sessão ausente ou expirada. */
  get isUnauthorized(): boolean {
    return this.statusCode === 401 || this.statusCode === 403;
  }
}

function parseRetryAfter(headers: Headers | undefined): number | null {
  const raw = headers?.get("retry-after");
  if (!raw) return null;

  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}

function toFieldErrors(
  details?: ApiErrorDetail[] | null,
): Record<string, string> {
  if (!details?.length) return {};

  return details.reduce<Record<string, string>>((acc, detail) => {
    const field = detail.path?.join(".");
    if (field && detail.message && !acc[field]) {
      acc[field] = detail.message;
    }
    return acc;
  }, {});
}

/**
 * Converte a resposta do `customFetch` no `data` do envelope, ou lança
 * `ReferralApiError` quando o status não é de sucesso.
 */
function unwrap<T>(
  response: { status: number; data: unknown; headers?: Headers },
  fallbackMessage: string,
): T {
  if (response.status >= 200 && response.status < 300) {
    const body = response.data as { data: T };
    return body.data;
  }

  const body = (response.data ?? {}) as ApiErrorEnvelope;

  throw new ReferralApiError({
    message: body.message ?? fallbackMessage,
    statusCode: body.error?.statusCode ?? response.status,
    code: body.error?.code ?? null,
    fieldErrors: toFieldErrors(body.error?.details),
    retryAfterSeconds: parseRetryAfter(response.headers),
    requestId: body.requestId ?? null,
  });
}

/**
 * Valida um código de indicação (rota pública).
 * Rate limit por IP — chame uma vez ao carregar a página; o servidor ainda
 * cacheia o resultado por 10 minutos.
 */
export async function validateReferralCode(
  code: string,
  signal?: AbortSignal,
): Promise<ValidatedReferralCode> {
  const response = await getReferralsValidate({ code }, { signal });
  return unwrap<ValidatedReferralCode>(
    response,
    "Não foi possível validar o convite.",
  );
}

/**
 * Registra a intenção de cadastro e dispara o OTP (rota pública).
 * `otpSent: false` significa que o e-mail já tem conta — nesse caso nenhum
 * código foi enviado por esta rota.
 */
export async function referralSignup(
  payload: StartReferralSignupDTO,
): Promise<StartReferralSignupResponse> {
  const response = await postReferralsSignup(payload);
  return unwrap<StartReferralSignupResponse>(
    response,
    "Não foi possível enviar seu código.",
  );
}

/**
 * Código de indicação do usuário logado.
 * `null` quando ele ainda não gerou o seu.
 */
export async function getMyReferral(): Promise<MyReferral> {
  const response = await getReferralsMe();
  return unwrap<MyReferral>(response, "Não foi possível carregar seu link.");
}

/**
 * Gera o código de indicação do usuário logado. Devolve só o código criado —
 * os totais e a lista de indicados vêm do `GET /referrals/me`.
 *
 * `code` é opcional: sem ele o servidor sorteia um. Com ele, o usuário escolhe
 * o próprio — e aí um 409 pode significar "código em uso" (`E_REFERRAL_CODE_TAKEN`),
 * não só "você já tem um" (`E_REFERRAL_CODE_ALREADY_EXISTS`).
 */
export async function createMyReferral(code?: string): Promise<ReferralCode> {
  const response = await postReferralsMe(code ? { code } : undefined);
  return unwrap<ReferralCode>(response, "Não foi possível gerar seu link.");
}

/**
 * Troca o código do usuário logado.
 *
 * As indicações já registradas são preservadas (apontam para o id do código,
 * não para o texto), mas **todo link já distribuído com o código antigo deixa
 * de funcionar** — avise antes de confirmar.
 *
 * 404 significa que o usuário ainda não gerou um código; 409, que o novo já
 * está em uso.
 */
export async function updateMyReferral(code: string): Promise<ReferralCode> {
  const response = await patchReferralsMe({ code });
  return unwrap<ReferralCode>(response, "Não foi possível alterar seu código.");
}

/** Normaliza como a entidade do backend: sem espaços nas bordas e em caixa alta. */
export function normalizeReferralCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * Espelha o formato validado pela entidade do backend, só para dar retorno
 * imediato enquanto o usuário digita. Devolve a mensagem de erro, ou `null`
 * quando o código passa.
 *
 * De propósito **não** replica a blocklist de termos reservados (ADMIN, WEDO,
 * ...): é uma lista que muda no backend e ficaria desatualizada aqui. Esses
 * casos voltam como 400 com a mensagem pronta do servidor, que continua sendo
 * a autoridade sobre o que é aceito.
 */
export function validateReferralCodeFormat(code: string): string | null {
  const normalized = normalizeReferralCode(code);

  if (!normalized) {
    return "Informe um código.";
  }

  if (!/^[A-Z0-9-]{4,20}$/.test(normalized)) {
    return "Use de 4 a 20 caracteres: letras, números e hífen.";
  }

  if (normalized.startsWith("-") || normalized.endsWith("-")) {
    return "O código não pode começar nem terminar com hífen.";
  }

  return null;
}
